// Core React Imports
import { useState, useEffect, useRef } from "react";

// Third-Party Libraries
import { useNavigate } from "react-router-dom";

// Local Imports
import ApiService from "../../data/ApiService";
import UtilsUnit from "../../utils/UtilsUnit";
import { useLoading } from "../../widgets/loadings/WidgetLoading";
import ConstantsTexts from "../../constants/ConstantsTexts";
import { useWidgetFilterDropdowns } from "../../widgets/dropdowns/widget-filter-dropdowns/useWidgetFilterDropdowns";
import useUtilsUnit from "../../utils/useUtilsUnit";
import { DropdownType } from "../../widgets/dropdowns/widget-dropdown/WidgetDropdown";
import ConstantsParams from "../../constants/ConstantsParams";

export function usePageModelInfo() {
  const navigate = useNavigate();
  const { userData } = useUtilsUnit({ userData: true });
  const [filterData, setFilterData] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const [data, setData] = useState([]);
  const [dataIsReceived, setDataIsReceived] = useState(false);
  const [showUpdatesList, setShowUpdatesList] = useState(false);
  const [isDialogEditOpen, setDialogEditOpen] = useState(false);
  const [isDialogSaveOpen, setDialogSaveOpen] = useState(false);
  const [isDialogUpdateModelFieldsOpen, setDialogUpdateModelFieldsOpen] = useState(false);
  const [showSearchTerminal, setShowSearchTerminal] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedPlanningSpace, setSelectedPlanningSpace] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [selectedModelVersion, setSelectedModelVersion] = useState(null);
  const [isCreatingNewModelVersion, setIsCreatingNewModelVersion] = useState(false);
  const [isBaseModelSelected, setIsBaseModelSelected] = useState(false);
  const [dropdownType, setDropdownType] = useState(DropdownType.DEFAULT_SELECT);

  // Data from API
  const [terminalTypesAndPrices, setTerminalTypesAndPrices] = useState(null);
  const [mainMeasure, setMainMeasure] = useState(null);
  const [modelChangesLog, setModelChangesLog] = useState(null);
  const [modelVersions, setModelVersions] = useState([]);

  // Input
  const [inputValueLifeTime, setInputValueLifeTime] = useState("");
  const [inputValueBusinessDays, setInputValueBusinessDays] = useState("");
  const [inputValueMetropolitanRingOne, setInputValueMetropolitanRingOne] = useState("");
  const [inputValueMetropolitanRingTwo, setInputValueMetropolitanRingTwo] = useState("");
  const [inputValueMetropolitanRingThree, setInputValueMetropolitanRingThree] = useState("");
  const [inputValueMetropolitanRingFour, setInputValueMetropolitanRingFour] = useState("");
  const [inputValueCostPerAcre, setInputValueCostPerAcre] = useState("");

  // Input Percent
  const [inputValueUrgencyPercent, setInputValueUrgencyPercent] = useState("");
  const [inputValueImportanceServicePercent, setInputValueImportanceServicePercent] = useState("");
  const [inputValuePotentialPercent, setInputValuePotentialPercent] = useState("");
  const [inputValueReducingOperationalGapPercent, setInputValueReducingOperationalGapPercent] = useState("");
  const [inputValueCostPerPassengerPercent, setInputValueCostPerPassengerPercent] = useState("");
  const [inputValueCostPerTripPercent, setInputValueCostPerTripPercent] = useState("");
  const [inputValuePositionLocalAuthorityPercent, setInputValuePositionLocalAuthorityPercent] = useState("");

  // Input One
  const [inputValueUrgencyOne, setInputValueUrgencyOne] = useState("");
  const [inputValueImportanceServiceOneFrom, setInputValueImportanceServiceOneFrom] = useState("");
  const [inputValueImportanceServiceOneTo, setInputValueImportanceServiceOneTo] = useState("");
  const [inputValuePotentialOneFrom, setInputValuePotentialOneFrom] = useState("");
  const [inputValuePotentialOneTo, setInputValuePotentialOneTo] = useState("");
  const [inputValueReducingOperationalGapOneFrom, setInputValueReducingOperationalGapOneFrom] = useState("");
  const [inputValueReducingOperationalGapOneTo, setInputValueReducingOperationalGapOneTo] = useState("");
  const [inputValueCostPerPassengerOneFrom, setInputValueCostPerPassengerOneFrom] = useState("");
  const [inputValueCostPerPassengerOneTo, setInputValueCostPerPassengerOneTo] = useState("");
  const [inputValueCostPerTripOneFrom, setInputValueCostPerTripOneFrom] = useState("");
  const [inputValueCostPerTripOneTo, setInputValueCostPerTripOneTo] = useState("");

  // Input Two
  const [inputValueUrgencyTwo, setInputValueUrgencyTwo] = useState("");
  const [inputValueImportanceServiceTwoFrom, setInputValueImportanceServiceTwoFrom] = useState("");
  const [inputValueImportanceServiceTwoTo, setInputValueImportanceServiceTwoTo] = useState("");
  const [inputValuePotentialTwoFrom, setInputValuePotentialTwoFrom] = useState("");
  const [inputValuePotentialTwoTo, setInputValuePotentialTwoTo] = useState("");
  const [inputValueReducingOperationalGapTwoFrom, setInputValueReducingOperationalGapTwoFrom] = useState("");
  const [inputValueReducingOperationalGapTwoTo, setInputValueReducingOperationalGapTwoTo] = useState("");
  const [inputValueCostPerPassengerTwoFrom, setInputValueCostPerPassengerTwoFrom] = useState("");
  const [inputValueCostPerPassengerTwoTo, setInputValueCostPerPassengerTwoTo] = useState("");
  const [inputValueCostPerTripTwoFrom, setInputValueCostPerTripTwoFrom] = useState("");
  const [inputValueCostPerTripTwoTo, setInputValueCostPerTripTwoTo] = useState("");

  // Input Three
  const [inputValueUrgencyThree, setInputValueUrgencyThree] = useState("");
  const [inputValueImportanceServiceThreeFrom, setInputValueImportanceServiceThreeFrom] = useState("");
  const [inputValueImportanceServiceThreeTo, setInputValueImportanceServiceThreeTo] = useState("");
  const [inputValuePotentialThreeFrom, setInputValuePotentialThreeFrom] = useState("");
  const [inputValuePotentialThreeTo, setInputValuePotentialThreeTo] = useState("");
  const [inputValueReducingOperationalGapThreeFrom, setInputValueReducingOperationalGapThreeFrom] = useState("");
  const [inputValueReducingOperationalGapThreeTo, setInputValueReducingOperationalGapThreeTo] = useState("");
  const [inputValueCostPerPassengerThreeFrom, setInputValueCostPerPassengerThreeFrom] = useState("");
  const [inputValueCostPerPassengerThreeTo, setInputValueCostPerPassengerThreeTo] = useState("");
  const [inputValueCostPerTripThreeFrom, setInputValueCostPerTripThreeFrom] = useState("");
  const [inputValueCostPerTripThreeTo, setInputValueCostPerTripThreeTo] = useState("");

  // Input Four
  const [inputValueUrgencyFour, setInputValueUrgencyFour] = useState("");
  const [inputValueImportanceServiceFourFrom, setInputValueImportanceServiceFourFrom] = useState("");
  const [inputValueImportanceServiceFourTo, setInputValueImportanceServiceFourTo] = useState("");
  const [inputValuePotentialFourFrom, setInputValuePotentialFourFrom] = useState("");
  const [inputValuePotentialFourTo, setInputValuePotentialFourTo] = useState("");
  const [inputValueReducingOperationalGapFourFrom, setInputValueReducingOperationalGapFourFrom] = useState("");
  const [inputValueReducingOperationalGapFourTo, setInputValueReducingOperationalGapFourTo] = useState("");
  const [inputValueCostPerPassengerFourFrom, setInputValueCostPerPassengerFourFrom] = useState("");
  const [inputValueCostPerPassengerFourTo, setInputValueCostPerPassengerFourTo] = useState("");
  const [inputValueCostPerTripFourFrom, setInputValueCostPerTripFourFrom] = useState("");
  const [inputValueCostPerTripFourTo, setInputValueCostPerTripFourTo] = useState("");

  // Input Five
  const [inputValueUrgencyFive, setInputValueUrgencyFive] = useState("");
  const [inputValueImportanceServiceFiveFrom, setInputValueImportanceServiceFiveFrom] = useState("");
  const [inputValueImportanceServiceFiveTo, setInputValueImportanceServiceFiveTo] = useState("");
  const [inputValuePotentialFiveFrom, setInputValuePotentialFiveFrom] = useState("");
  const [inputValuePotentialFiveTo, setInputValuePotentialFiveTo] = useState("");
  const [inputValueReducingOperationalGapFiveFrom, setInputValueReducingOperationalGapFiveFrom] = useState("");
  const [inputValueReducingOperationalGapFiveTo, setInputValueReducingOperationalGapFiveTo] = useState("");
  const [inputValueCostPerPassengerFiveFrom, setInputValueCostPerPassengerFiveFrom] = useState("");
  const [inputValueCostPerPassengerFiveTo, setInputValueCostPerPassengerFiveTo] = useState("");
  const [inputValueCostPerTripFiveFrom, setInputValueCostPerTripFiveFrom] = useState("");
  const [inputValueCostPerTripFiveTo, setInputValueCostPerTripFiveTo] = useState("");

  // Input Costs
  // Input Costs One
  const [inputValueMonthsOfWorkOne, setInputValueMonthsOfWorkOne] = useState("");
  const [inputValueCostOfBuildingsOne, setInputValueCostOfBuildingsOne] = useState("");
  const [inputValueCostOfTrafficOne, setInputValueCostOfTrafficOne] = useState("");

  // Input Costs Two
  const [inputValueMonthsOfWorkTwo, setInputValueMonthsOfWorkTwo] = useState("");
  const [inputValueCostOfBuildingsTwo, setInputValueCostOfBuildingsTwo] = useState("");
  const [inputValueCostOfTrafficTwo, setInputValueCostOfTrafficTwo] = useState("");

  // Input Costs Three
  const [inputValueMonthsOfWorkThree, setInputValueMonthsOfWorkThree] = useState("");
  const [inputValueCostOfBuildingsThree, setInputValueCostOfBuildingsThree] = useState("");
  const [inputValueCostOfTrafficThree, setInputValueCostOfTrafficThree] = useState("");

  // Input Costs Four
  const [inputValueMonthsOfWorkFour, setInputValueMonthsOfWorkFour] = useState("");
  const [inputValueCostOfBuildingsFour, setInputValueCostOfBuildingsFour] = useState("");
  const [inputValueCostOfTrafficFour, setInputValueCostOfTrafficFour] = useState("");

  // Input Costs Five
  const [inputValueMonthsOfWorkFive, setInputValueMonthsOfWorkFive] = useState("");
  const [inputValueCostOfBuildingsFive, setInputValueCostOfBuildingsFive] = useState("");
  const [inputValueCostOfTrafficFive, setInputValueCostOfTrafficFive] = useState("");

  const jsonEditRef = useRef({});
  const jsonEditRefCheckChanges = useRef({});
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  const lastActionRef = useRef(null);
  const lastResponseDataRef = useRef(null);
  const baseModelSelectedVersionRef = useRef(null);
  const originalSelectedModelVersionRef = useRef(null);
  const modelVersionToDeleteRef = useRef(null);
  const modelVersionToFreezeRef = useRef(null);

  const workMonthsText = ConstantsTexts.CODE_111;
  const buildingCostText = ConstantsTexts.CODE_56;
  const monthlyTrafficArrangementCostText = ConstantsTexts.CODE_112;
  const totalTrafficArrangementCostText = ConstantsTexts.CODE_113;

  // useWidgetFilterDropdowns - start
  const { uniqueDistricts, uniquePlanningSpace, uniqueMunicipalities, uniqueTerminals } = useWidgetFilterDropdowns(
    filterData,
    selectedDistrict,
    selectedPlanningSpace,
    selectedMunicipality
  );
  // useWidgetFilterDropdowns - end

  // Fetch data on component mount
  useEffect(() => {
    (async () => {
      await fetchFilterData();
      await fetchData(0);
      setDataIsReceived(true);
    })();
  }, []);

  // Set initial selected model version when modelVersions data is loaded
  useEffect(() => {
    if (modelVersions && modelVersions.length > 0 && !selectedModelVersion) {
      // Select the first model version by default
      setSelectedModelVersion(modelVersions[0]);
    }
  }, [modelVersions, selectedModelVersion]);

  // Fetch filter data function
  const fetchFilterData = async () => {
    showLoadingRef.current();

    try {
      const filterDataResponse = await ApiService.getDataForFiltering();
      setFilterData(filterDataResponse);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Fetches model data from API and processes the response
  const fetchData = async (modelVersionInt) => {
    showLoadingRef.current();

    try {
      const fetchedData = await ApiService.getModelData(modelVersionInt);
      handleFechedAPIData(fetchedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Processes fetched API data by setting state, populating inputs, and initializing change tracking
  function handleFechedAPIData(fetchedData) {
    setData(fetchedData);
    setDataFromFetchedData(fetchedData);
    setDataToInputs(fetchedData);
    setJsonEditRefCheckChangesData();
    clearBaseModel();
  }

  function setDataFromFetchedData(fetchedData) {
    setTerminalTypesAndPrices(fetchedData.terminalTypesAndPrices);
    setMainMeasure(fetchedData.mainMeasure);
    setModelChangesLog(fetchedData.modelChangesLog);
    setModelVersions(fetchedData?.modelVersions);
  }

  // Populates all input fields with data from API response, mapping complex nested data to individual input states
  function setDataToInputs(data) {
    var secondaryMeasure = data.secondaryMeasure;
    var potentialMeasure = data.potentialMeasure;
    var operationalGapsMeasure = data.operationalGapsMeasure;
    var importanceMeasure = data.importanceMeasure;
    var urgencyMeasure = data.urgencyMeasure;
    var costPerPassengerMeasure = data.costPerPassengerMeasure;
    var costPerTripMeasure = data.costPerTripMeasure;
    var fillFactorByRing = data.fillFactorByRing;
    var terminalTypesAndPrices = data.terminalTypesAndPrices;
    var economicsParameters = data.economicsParameters;

    // secondaryMeasure
    setInputValueUrgencyPercent(getSecondaryMeasurePercent(secondaryMeasure, 1));
    setInputValueImportanceServicePercent(getSecondaryMeasurePercent(secondaryMeasure, 2));
    setInputValuePotentialPercent(getSecondaryMeasurePercent(secondaryMeasure, 3));
    setInputValueReducingOperationalGapPercent(getSecondaryMeasurePercent(secondaryMeasure, 4));
    setInputValueCostPerPassengerPercent(getSecondaryMeasurePercent(secondaryMeasure, 5));
    setInputValueCostPerTripPercent(getSecondaryMeasurePercent(secondaryMeasure, 6));
    setInputValuePositionLocalAuthorityPercent(getSecondaryMeasurePercent(secondaryMeasure, 7));

    // potentialMeasure - from
    setInputValuePotentialOneFrom(getFromPopulation(potentialMeasure, 1));
    setInputValuePotentialTwoFrom(getFromPopulation(potentialMeasure, 2));
    setInputValuePotentialThreeFrom(getFromPopulation(potentialMeasure, 3));
    setInputValuePotentialFourFrom(getFromPopulation(potentialMeasure, 4));
    setInputValuePotentialFiveFrom(getFromPopulation(potentialMeasure, 5));

    // potentialMeasure - to
    setInputValuePotentialOneTo(getToPopulation(potentialMeasure, 1));
    setInputValuePotentialTwoTo(getToPopulation(potentialMeasure, 2));
    setInputValuePotentialThreeTo(getToPopulation(potentialMeasure, 3));
    setInputValuePotentialFourTo(getToPopulation(potentialMeasure, 4));
    setInputValuePotentialFiveTo(getToPopulation(potentialMeasure, 5));

    // operationalGapsMeasure - from
    setInputValueReducingOperationalGapOneFrom(getFromRatio(operationalGapsMeasure, 1));
    setInputValueReducingOperationalGapTwoFrom(getFromRatio(operationalGapsMeasure, 2));
    setInputValueReducingOperationalGapThreeFrom(getFromRatio(operationalGapsMeasure, 3));
    setInputValueReducingOperationalGapFourFrom(getFromRatio(operationalGapsMeasure, 4));
    setInputValueReducingOperationalGapFiveFrom(getFromRatio(operationalGapsMeasure, 5));

    // operationalGapsMeasure - to
    setInputValueReducingOperationalGapOneTo(getToRatio(operationalGapsMeasure, 1));
    setInputValueReducingOperationalGapTwoTo(getToRatio(operationalGapsMeasure, 2));
    setInputValueReducingOperationalGapThreeTo(getToRatio(operationalGapsMeasure, 3));
    setInputValueReducingOperationalGapFourTo(getToRatio(operationalGapsMeasure, 4));
    setInputValueReducingOperationalGapFiveTo(getToRatio(operationalGapsMeasure, 5));

    // importanceMeasure - from
    setInputValueImportanceServiceOneFrom(getFromNumberOfTerminalsInArea(importanceMeasure, 1));
    setInputValueImportanceServiceTwoFrom(getFromNumberOfTerminalsInArea(importanceMeasure, 2));
    setInputValueImportanceServiceThreeFrom(getFromNumberOfTerminalsInArea(importanceMeasure, 3));
    setInputValueImportanceServiceFourFrom(getFromNumberOfTerminalsInArea(importanceMeasure, 4));
    setInputValueImportanceServiceFiveFrom(getFromNumberOfTerminalsInArea(importanceMeasure, 5));

    // importanceMeasure - to
    setInputValueImportanceServiceOneTo(getToNumberOfTerminalsInArea(importanceMeasure, 1));
    setInputValueImportanceServiceTwoTo(getToNumberOfTerminalsInArea(importanceMeasure, 2));
    setInputValueImportanceServiceThreeTo(getToNumberOfTerminalsInArea(importanceMeasure, 3));
    setInputValueImportanceServiceFourTo(getToNumberOfTerminalsInArea(importanceMeasure, 4));
    setInputValueImportanceServiceFiveTo(getToNumberOfTerminalsInArea(importanceMeasure, 5));

    // urgencyMeasure
    setInputValueUrgencyOne(getUrgencyMeasureValue(urgencyMeasure, 1));
    setInputValueUrgencyTwo(getUrgencyMeasureValue(urgencyMeasure, 2));
    setInputValueUrgencyThree(getUrgencyMeasureValue(urgencyMeasure, 3));
    setInputValueUrgencyFour(getUrgencyMeasureValue(urgencyMeasure, 4));
    setInputValueUrgencyFive(getUrgencyMeasureValue(urgencyMeasure, 5));

    // costPerPassengerMeasure - from
    setInputValueCostPerPassengerOneFrom(getFromCostPerPassenger(costPerPassengerMeasure, 1));
    setInputValueCostPerPassengerTwoFrom(getFromCostPerPassenger(costPerPassengerMeasure, 2));
    setInputValueCostPerPassengerThreeFrom(getFromCostPerPassenger(costPerPassengerMeasure, 3));
    setInputValueCostPerPassengerFourFrom(getFromCostPerPassenger(costPerPassengerMeasure, 4));
    setInputValueCostPerPassengerFiveFrom(getFromCostPerPassenger(costPerPassengerMeasure, 5));

    // costPerPassengerMeasure - to
    setInputValueCostPerPassengerOneTo(getToCostPerPassenger(costPerPassengerMeasure, 1));
    setInputValueCostPerPassengerTwoTo(getToCostPerPassenger(costPerPassengerMeasure, 2));
    setInputValueCostPerPassengerThreeTo(getToCostPerPassenger(costPerPassengerMeasure, 3));
    setInputValueCostPerPassengerFourTo(getToCostPerPassenger(costPerPassengerMeasure, 4));
    setInputValueCostPerPassengerFiveTo(getToCostPerPassenger(costPerPassengerMeasure, 5));

    // costPerTripMeasure - from
    setInputValueCostPerTripOneFrom(getFromCostPerTrip(costPerTripMeasure, 1));
    setInputValueCostPerTripTwoFrom(getFromCostPerTrip(costPerTripMeasure, 2));
    setInputValueCostPerTripThreeFrom(getFromCostPerTrip(costPerTripMeasure, 3));
    setInputValueCostPerTripFourFrom(getFromCostPerTrip(costPerTripMeasure, 4));
    setInputValueCostPerTripFiveFrom(getFromCostPerTrip(costPerTripMeasure, 5));

    // costPerTripMeasure - to
    setInputValueCostPerTripOneTo(getToCostPerTrip(costPerTripMeasure, 1));
    setInputValueCostPerTripTwoTo(getToCostPerTrip(costPerTripMeasure, 2));
    setInputValueCostPerTripThreeTo(getToCostPerTrip(costPerTripMeasure, 3));
    setInputValueCostPerTripFourTo(getToCostPerTrip(costPerTripMeasure, 4));
    setInputValueCostPerTripFiveTo(getToCostPerTrip(costPerTripMeasure, 5));

    // fillFactorByRing
    setInputValueMetropolitanRingOne(getFillFactor(fillFactorByRing, 1));
    setInputValueMetropolitanRingTwo(getFillFactor(fillFactorByRing, 2));
    setInputValueMetropolitanRingThree(getFillFactor(fillFactorByRing, 3));
    setInputValueMetropolitanRingFour(getFillFactor(fillFactorByRing, 4));

    // terminalTypesAndPrices - pricePerAcreNIS
    setInputValueCostPerAcre(terminalTypesAndPrices[0].pricePerAcreNIS);

    // terminalTypesAndPrices - monthsOfWork
    setInputValueMonthsOfWorkOne(getMonthsOfWork(terminalTypesAndPrices, 1));
    setInputValueMonthsOfWorkTwo(getMonthsOfWork(terminalTypesAndPrices, 2));
    setInputValueMonthsOfWorkThree(getMonthsOfWork(terminalTypesAndPrices, 3));
    setInputValueMonthsOfWorkFour(getMonthsOfWork(terminalTypesAndPrices, 4));
    setInputValueMonthsOfWorkFive(getMonthsOfWork(terminalTypesAndPrices, 5));

    // terminalTypesAndPrices - priceForBuildingsNIS
    setInputValueCostOfBuildingsOne(getPriceForBuildingsNIS(terminalTypesAndPrices, 1));
    setInputValueCostOfBuildingsTwo(getPriceForBuildingsNIS(terminalTypesAndPrices, 2));
    setInputValueCostOfBuildingsThree(getPriceForBuildingsNIS(terminalTypesAndPrices, 3));
    setInputValueCostOfBuildingsFour(getPriceForBuildingsNIS(terminalTypesAndPrices, 4));
    setInputValueCostOfBuildingsFive(getPriceForBuildingsNIS(terminalTypesAndPrices, 5));

    // terminalTypesAndPrices - priceForTrafficArrangementsPerMonthNIS
    setInputValueCostOfTrafficOne(getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 1));
    setInputValueCostOfTrafficTwo(getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 2));
    setInputValueCostOfTrafficThree(getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 3));
    setInputValueCostOfTrafficFour(getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 4));
    setInputValueCostOfTrafficFive(getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 5));

    // economicsParameters
    setInputValueLifeTime(getEconomicsParametersParameter(economicsParameters, 2));
    setInputValueBusinessDays(getEconomicsParametersParameter(economicsParameters, 1));

    // modelVersions
    setSelectedModelVersionFromInt(data.modelVersion, data?.modelVersions);

    // JSON
    jsonEditRef.current = {
      // secondaryMeasure
      urgency_percent: getSecondaryMeasurePercent(secondaryMeasure, 1),
      importance_service_percent: getSecondaryMeasurePercent(secondaryMeasure, 2),
      potential_percent: getSecondaryMeasurePercent(secondaryMeasure, 3),
      reducing_operational_gap_percent: getSecondaryMeasurePercent(secondaryMeasure, 4),
      cost_per_passenger_percent: getSecondaryMeasurePercent(secondaryMeasure, 5),
      cost_per_trip_percent: getSecondaryMeasurePercent(secondaryMeasure, 6),
      position_local_authority_percent: getSecondaryMeasurePercent(secondaryMeasure, 7),

      // potentialMeasure
      potential_one_from: getFromPopulation(potentialMeasure, 1),
      potential_one_to: getToPopulation(potentialMeasure, 1),
      potential_two_from: getFromPopulation(potentialMeasure, 2),
      potential_two_to: getToPopulation(potentialMeasure, 2),
      potential_three_from: getFromPopulation(potentialMeasure, 3),
      potential_three_to: getToPopulation(potentialMeasure, 3),
      potential_four_from: getFromPopulation(potentialMeasure, 4),
      potential_four_to: getToPopulation(potentialMeasure, 4),
      potential_five_from: getFromPopulation(potentialMeasure, 5),
      potential_five_to: getToPopulation(potentialMeasure, 5),

      // operationalGapsMeasure
      reducing_operational_gap_one_from: getFromRatio(operationalGapsMeasure, 1),
      reducing_operational_gap_one_to: getToRatio(operationalGapsMeasure, 1),
      reducing_operational_gap_two_from: getFromRatio(operationalGapsMeasure, 2),
      reducing_operational_gap_two_to: getToRatio(operationalGapsMeasure, 2),
      reducing_operational_gap_three_from: getFromRatio(operationalGapsMeasure, 3),
      reducing_operational_gap_three_to: getToRatio(operationalGapsMeasure, 3),
      reducing_operational_gap_four_from: getFromRatio(operationalGapsMeasure, 4),
      reducing_operational_gap_four_to: getToRatio(operationalGapsMeasure, 4),
      reducing_operational_gap_five_from: getFromRatio(operationalGapsMeasure, 5),
      reducing_operational_gap_five_to: getToRatio(operationalGapsMeasure, 5),

      // importanceMeasure
      importance_service_one_from: getFromNumberOfTerminalsInArea(importanceMeasure, 1),
      importance_service_one_to: getToNumberOfTerminalsInArea(importanceMeasure, 1),
      importance_service_two_from: getFromNumberOfTerminalsInArea(importanceMeasure, 2),
      importance_service_two_to: getToNumberOfTerminalsInArea(importanceMeasure, 2),
      importance_service_three_from: getFromNumberOfTerminalsInArea(importanceMeasure, 3),
      importance_service_three_to: getToNumberOfTerminalsInArea(importanceMeasure, 3),
      importance_service_four_from: getFromNumberOfTerminalsInArea(importanceMeasure, 4),
      importance_service_four_to: getToNumberOfTerminalsInArea(importanceMeasure, 4),
      importance_service_five_from: getFromNumberOfTerminalsInArea(importanceMeasure, 5),
      importance_service_five_to: getToNumberOfTerminalsInArea(importanceMeasure, 5),

      // urgencyMeasur
      urgency_one: getUrgencyMeasureValue(urgencyMeasure, 1),
      urgency_two: getUrgencyMeasureValue(urgencyMeasure, 2),
      urgency_three: getUrgencyMeasureValue(urgencyMeasure, 3),
      urgency_four: getUrgencyMeasureValue(urgencyMeasure, 4),
      urgency_five: getUrgencyMeasureValue(urgencyMeasure, 5),

      // costPerPassengerMeasure
      cost_per_passenger_one_from: getFromCostPerPassenger(costPerPassengerMeasure, 1),
      cost_per_passenger_one_to: getToCostPerPassenger(costPerPassengerMeasure, 1),
      cost_per_passenger_two_from: getFromCostPerPassenger(costPerPassengerMeasure, 2),
      cost_per_passenger_two_to: getToCostPerPassenger(costPerPassengerMeasure, 2),
      cost_per_passenger_three_from: getFromCostPerPassenger(costPerPassengerMeasure, 3),
      cost_per_passenger_three_to: getToCostPerPassenger(costPerPassengerMeasure, 3),
      cost_per_passenger_four_from: getFromCostPerPassenger(costPerPassengerMeasure, 4),
      cost_per_passenger_four_to: getToCostPerPassenger(costPerPassengerMeasure, 4),
      cost_per_passenger_five_from: getFromCostPerPassenger(costPerPassengerMeasure, 5),
      cost_per_passenger_five_to: getToCostPerPassenger(costPerPassengerMeasure, 5),

      // costPerTripMeasure
      cost_per_trip_one_from: getFromCostPerTrip(costPerTripMeasure, 1),
      cost_per_trip_one_to: getToCostPerTrip(costPerTripMeasure, 1),
      cost_per_trip_two_from: getFromCostPerTrip(costPerTripMeasure, 2),
      cost_per_trip_two_to: getToCostPerTrip(costPerTripMeasure, 2),
      cost_per_trip_three_from: getFromCostPerTrip(costPerTripMeasure, 3),
      cost_per_trip_three_to: getToCostPerTrip(costPerTripMeasure, 3),
      cost_per_trip_four_from: getFromCostPerTrip(costPerTripMeasure, 4),
      cost_per_trip_four_to: getToCostPerTrip(costPerTripMeasure, 4),
      cost_per_trip_five_from: getFromCostPerTrip(costPerTripMeasure, 5),
      cost_per_trip_five_to: getToCostPerTrip(costPerTripMeasure, 5),

      // fillFactorByRing
      metropolitan_ring_one: getFillFactor(fillFactorByRing, 1),
      metropolitan_ring_two: getFillFactor(fillFactorByRing, 2),
      metropolitan_ring_three: getFillFactor(fillFactorByRing, 3),
      metropolitan_ring_four: getFillFactor(fillFactorByRing, 4),

      // terminalTypesAndPrices
      cost_per_acre: terminalTypesAndPrices[0].pricePerAcreNIS,
      months_of_work_one: getMonthsOfWork(terminalTypesAndPrices, 1),
      cost_of_buildings_one: getPriceForBuildingsNIS(terminalTypesAndPrices, 1),
      cost_of_traffic_one: getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 1),
      months_of_work_two: getMonthsOfWork(terminalTypesAndPrices, 2),
      cost_of_buildings_two: getPriceForBuildingsNIS(terminalTypesAndPrices, 2),
      cost_of_traffic_two: getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 2),
      months_of_work_three: getMonthsOfWork(terminalTypesAndPrices, 3),
      cost_of_buildings_three: getPriceForBuildingsNIS(terminalTypesAndPrices, 3),
      cost_of_traffic_three: getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 3),
      months_of_work_four: getMonthsOfWork(terminalTypesAndPrices, 4),
      cost_of_buildings_four: getPriceForBuildingsNIS(terminalTypesAndPrices, 4),
      cost_of_traffic_four: getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 4),
      months_of_work_five: getMonthsOfWork(terminalTypesAndPrices, 5),
      cost_of_buildings_five: getPriceForBuildingsNIS(terminalTypesAndPrices, 5),
      cost_of_traffic_five: getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, 5),

      // economicsParameters
      life_time: getEconomicsParametersParameter(economicsParameters, 2),
      business_days: getEconomicsParametersParameter(economicsParameters, 1),
    };
  }

  function setJsonEditRefCheckChangesData() {
    jsonEditRefCheckChanges.current = { ...jsonEditRef.current };
  }

  // Get value by serial
  function getSecondaryMeasurePercent(secondaryMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, secondaryMeasure, "secondaryMeasureSerial", "secondaryMeasurePercent");
  }

  function getFromPopulation(potentialMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, potentialMeasure, "potentialMeasureGrade", "fromPopulation");
  }

  function getToPopulation(potentialMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, potentialMeasure, "potentialMeasureGrade", "toPopulation");
  }

  function getFromRatio(operationalGapsMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, operationalGapsMeasure, "operationalGapsGrade", "fromRatio");
  }

  function getToRatio(operationalGapsMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, operationalGapsMeasure, "operationalGapsGrade", "toRatio");
  }

  function getFromNumberOfTerminalsInArea(importanceMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, importanceMeasure, "importanceGrade", "fromNumberOfTerminalsInArea");
  }

  function getToNumberOfTerminalsInArea(importanceMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, importanceMeasure, "importanceGrade", "toNumberOfTerminalsInArea");
  }

  function getUrgencyMeasureValue(urgencyMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, urgencyMeasure, "urgencyMeasureGrade", "urgencyMeasureValue");
  }

  function getFromCostPerPassenger(costPerPassengerMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, costPerPassengerMeasure, "costPerPassengerGrade", "fromCostPerPassenger");
  }

  function getToCostPerPassenger(costPerPassengerMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, costPerPassengerMeasure, "costPerPassengerGrade", "toCostPerPassenger");
  }

  function getFromCostPerTrip(costPerTripMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, costPerTripMeasure, "costPerTripGrade", "fromCostPerTrip");
  }

  function getToCostPerTrip(costPerTripMeasure, serial) {
    return UtilsUnit.getValueFromList(serial, costPerTripMeasure, "costPerTripGrade", "toCostPerTrip");
  }

  function getFillFactor(fillFactorByRing, serial) {
    return UtilsUnit.getValueFromList(serial, fillFactorByRing, "ringINT", "fillFactor");
  }

  function getEconomicsParametersParameter(economicsParameters, serial) {
    return UtilsUnit.getValueFromList(serial, economicsParameters, "parameterSerial", "parameter");
  }

  function getMonthsOfWork(terminalTypesAndPrices, serial) {
    return UtilsUnit.getValueFromList(serial, terminalTypesAndPrices, "terminalTypeSerial", "monthsOfWork");
  }

  function getPriceForBuildingsNIS(terminalTypesAndPrices, serial) {
    return UtilsUnit.getValueFromList(serial, terminalTypesAndPrices, "terminalTypeSerial", "priceForBuildingsNIS");
  }

  function getPriceForTrafficArrangementsPerMonthNIS(terminalTypesAndPrices, serial) {
    return UtilsUnit.getValueFromList(serial, terminalTypesAndPrices, "terminalTypeSerial", "priceForTrafficArrangementsPerMonthNIS");
  }

  function getTotalPriceForTrafficArrangementsPerTerminalTypeNIS(serial) {
    return UtilsUnit.getValueFromList(serial, terminalTypesAndPrices, "terminalTypeSerial", "TotalPriceForTrafficArrangementsPerTerminalTypeNIS");
  }

  function getMainMeasurePercent(serial) {
    return UtilsUnit.getValueFromList(serial, mainMeasure, "mainMeasureSerial", "mainMeasurePercent");
  }

  // handle
  // Handles model version selection with support for base model mode and regular mode
  const handleSelectModelVersion = async (option) => {
    if (option === null) {
      if (dropdownType === DropdownType.SINGLE_SELECT) {
        baseModelSelectedVersionRef.current = null;
      } else {
        setSelectedModelVersion(null);
      }
      return;
    }

    // If in SINGLE_SELECT mode (base model selected), store in ref instead of state
    if (dropdownType === DropdownType.SINGLE_SELECT) {
      baseModelSelectedVersionRef.current = option;
    } else {
      setSelectedModelVersion(option);
      fetchData(option.modelVersionInt);
    }
  };

  // Validates form data and initiates save process with specified action type
  const handleSaveModelChanges = async (action) => {
    if (!handleValidations()) return;

    // Set last action
    lastActionRef.current = action;
    handleConfirmSaveModelChanges(action);
  };

  // Sends model data to API with user info and action type, handles response and shows confirmation dialog
  const handleConfirmSaveModelChanges = async (action) => {
    showLoadingRef.current();

    try {
      jsonEditRef.current.userID = UtilsUnit.getUserID(userData);
      jsonEditRef.current.userName = UtilsUnit.getUserName(userData);
      jsonEditRef.current.modelVersion = getAppropriateModelVersion()?.modelVersionInt;
      jsonEditRef.current.action = action;

      const response = await ApiService.updateModelData(jsonEditRef.current);

      if (response.status === 200) {
        lastResponseDataRef.current = response.data;
        showSaveAlertDialog();
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Runs all validation checks in sequence, returns false if any validation fails
  function handleValidations() {
    var isValid = true;

    if (!handleFirstValidation()) {
      isValid = false;
    } else if (!handleSecondValidation()) {
      isValid = false;
    } else if (!handleThirdValidation()) {
      isValid = false;
    } else if (!handleFourthValidation()) {
      isValid = false;
    } else if (!handleFifthValidation()) {
      isValid = false;
    } else if (!handleSixthValidation()) {
      isValid = false;
    }

    return isValid;
  }

  // Validates that all percentage fields sum to exactly 100%
  function handleFirstValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_78;
    var errorSecondLine = ConstantsTexts.CODE_79;

    const urgencyPercent = Number(inputValueUrgencyPercent);
    const importanceServicePercent = Number(inputValueImportanceServicePercent);
    const potentialPercent = Number(inputValuePotentialPercent);
    const reducingOperationalGapPercent = Number(inputValueReducingOperationalGapPercent);
    const costPerPassengerPercent = Number(inputValueCostPerPassengerPercent);
    const costPerTripPercent = Number(inputValueCostPerTripPercent);
    const positionLocalAuthorityPercent = Number(inputValuePositionLocalAuthorityPercent);

    const sumValues =
      urgencyPercent + importanceServicePercent + potentialPercent + reducingOperationalGapPercent + costPerPassengerPercent + costPerTripPercent + positionLocalAuthorityPercent;

    if (sumValues !== 100) {
      alert(errorFirstLine + "\n" + errorSecondLine);

      isValid = false;
    }

    return isValid;
  }

  // Validates that all importance service fields are in the correct order and that the values are increasing
  function handleSecondValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_80;
    var errorSecondLine = ConstantsTexts.CODE_81;
    var errorThirdLine = ConstantsTexts.CODE_82;
    var errorFourthLine = ConstantsTexts.CODE_83;
    var errorFifthLine = ConstantsTexts.CODE_79;

    const importanceServiceOneFrom = Number(inputValueImportanceServiceOneFrom);
    const importanceServiceOneTo = Number(inputValueImportanceServiceOneTo);
    const importanceServiceTwoFrom = Number(inputValueImportanceServiceTwoFrom);
    const importanceServiceTwoTo = Number(inputValueImportanceServiceTwoTo);
    const importanceServiceThreeFrom = Number(inputValueImportanceServiceThreeFrom);
    const importanceServiceThreeTo = Number(inputValueImportanceServiceThreeTo);
    const importanceServiceFourFrom = Number(inputValueImportanceServiceFourFrom);
    const importanceServiceFourTo = Number(inputValueImportanceServiceFourTo);
    const importanceServiceFiveFrom = Number(inputValueImportanceServiceFiveFrom);
    const importanceServiceFiveTo = Number(inputValueImportanceServiceFiveTo);

    var firstCondition = importanceServiceFiveFrom === 0;
    var secondCondition =
      importanceServiceFiveTo === importanceServiceFourFrom &&
      importanceServiceFourTo === importanceServiceThreeFrom &&
      importanceServiceThreeTo === importanceServiceTwoFrom &&
      importanceServiceTwoTo === importanceServiceOneFrom;
    var thirdCondition =
      importanceServiceOneTo > importanceServiceOneFrom &&
      importanceServiceTwoTo > importanceServiceTwoFrom &&
      importanceServiceThreeTo > importanceServiceThreeFrom &&
      importanceServiceFourTo > importanceServiceFourFrom &&
      importanceServiceFiveTo > importanceServiceFiveFrom;

    if (!firstCondition || !secondCondition || !thirdCondition) {
      alert(errorFirstLine + "\n" + errorSecondLine + "\n" + errorThirdLine + "\n" + errorFourthLine + "\n" + errorFifthLine);

      isValid = false;
    }

    return isValid;
  }

  // Validates that all potential fields are in the correct order and that the values are increasing
  function handleThirdValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_85;
    var errorSecondLine = ConstantsTexts.CODE_86;
    var errorThirdLine = ConstantsTexts.CODE_87;
    var errorFourthLine = ConstantsTexts.CODE_88;
    var errorFifthLine = ConstantsTexts.CODE_79;

    const potentialOneFrom = Number(inputValuePotentialOneFrom);
    const potentialOneTo = Number(inputValuePotentialOneTo);
    const potentialTwoFrom = Number(inputValuePotentialTwoFrom);
    const potentialTwoTo = Number(inputValuePotentialTwoTo);
    const potentialThreeFrom = Number(inputValuePotentialThreeFrom);
    const potentialThreeTo = Number(inputValuePotentialThreeTo);
    const potentialFourFrom = Number(inputValuePotentialFourFrom);
    const potentialFourTo = Number(inputValuePotentialFourTo);
    const potentialFiveFrom = Number(inputValuePotentialFiveFrom);
    const potentialFiveTo = Number(inputValuePotentialFiveTo);

    var firstCondition = potentialOneFrom === 0;
    var secondCondition =
      potentialOneTo === potentialTwoFrom && potentialTwoTo === potentialThreeFrom && potentialThreeTo === potentialFourFrom && potentialFourTo === potentialFiveFrom;
    var thirdCondition =
      potentialOneTo > potentialOneFrom &&
      potentialTwoTo > potentialTwoFrom &&
      potentialThreeTo > potentialThreeFrom &&
      potentialFourTo > potentialFourFrom &&
      potentialFiveTo > potentialFiveFrom;

    if (!firstCondition || !secondCondition || !thirdCondition) {
      alert(errorFirstLine + "\n" + errorSecondLine + "\n" + errorThirdLine + "\n" + errorFourthLine + "\n" + errorFifthLine);

      isValid = false;
    }

    return isValid;
  }

  // Validates that all reducing operational gap fields are in the correct order and that the values are increasing
  function handleFourthValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_90;
    var errorSecondLine = ConstantsTexts.CODE_91;
    var errorThirdLine = ConstantsTexts.CODE_92;
    var errorFourthLine = ConstantsTexts.CODE_93;
    var errorFifthLine = ConstantsTexts.CODE_79;

    const reducingOperationalGapOneFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapOneFrom);
    const reducingOperationalGapOneTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapOneTo);
    const reducingOperationalGapTwoFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapTwoFrom);
    const reducingOperationalGapTwoTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapTwoTo);
    const reducingOperationalGapThreeFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapThreeFrom);
    const reducingOperationalGapThreeTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapThreeTo);
    const reducingOperationalGapFourFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapFourFrom);
    const reducingOperationalGapFourTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapFourTo);
    const reducingOperationalGapFiveFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapFiveFrom);
    const reducingOperationalGapFiveTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueReducingOperationalGapFiveTo);

    var firstCondition = reducingOperationalGapOneFrom === 0;
    var secondCondition =
      reducingOperationalGapOneTo === reducingOperationalGapTwoFrom &&
      reducingOperationalGapTwoTo === reducingOperationalGapThreeFrom &&
      reducingOperationalGapThreeTo === reducingOperationalGapFourFrom &&
      reducingOperationalGapFourTo === reducingOperationalGapFiveFrom;
    var thirdCondition =
      reducingOperationalGapOneTo > reducingOperationalGapOneFrom &&
      reducingOperationalGapTwoTo > reducingOperationalGapTwoFrom &&
      reducingOperationalGapThreeTo > reducingOperationalGapThreeFrom &&
      reducingOperationalGapFourTo > reducingOperationalGapFourFrom &&
      reducingOperationalGapFiveTo > reducingOperationalGapFiveFrom;

    if (!firstCondition || !secondCondition || !thirdCondition) {
      alert(errorFirstLine + "\n" + errorSecondLine + "\n" + errorThirdLine + "\n" + errorFourthLine + "\n" + errorFifthLine);

      isValid = false;
    }

    return isValid;
  }

  // Validates that all cost per passenger fields are in the correct order and that the values are increasing
  function handleFifthValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_95;
    var errorSecondLine = ConstantsTexts.CODE_96;
    var errorThirdLine = ConstantsTexts.CODE_97;
    var errorFourthLine = ConstantsTexts.CODE_98;
    var errorFifthLine = ConstantsTexts.CODE_79;

    const costPerPassengerOneFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerOneFrom);
    const costPerPassengerOneTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerOneTo);
    const costPerPassengerTwoFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerTwoFrom);
    const costPerPassengerTwoTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerTwoTo);
    const costPerPassengerThreeFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerThreeFrom);
    const costPerPassengerThreeTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerThreeTo);
    const costPerPassengerFourFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerFourFrom);
    const costPerPassengerFourTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerFourTo);
    const costPerPassengerFiveFrom = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerFiveFrom);
    const costPerPassengerFiveTo = UtilsUnit.toNumberWithoutTrailingZeros(inputValueCostPerPassengerFiveTo);

    var firstCondition = costPerPassengerFiveFrom === 0;
    var secondCondition =
      costPerPassengerFiveTo === costPerPassengerFourFrom &&
      costPerPassengerFourTo === costPerPassengerThreeFrom &&
      costPerPassengerThreeTo === costPerPassengerTwoFrom &&
      costPerPassengerTwoTo === costPerPassengerOneFrom;
    var thirdCondition =
      costPerPassengerOneTo > costPerPassengerOneFrom &&
      costPerPassengerTwoTo > costPerPassengerTwoFrom &&
      costPerPassengerThreeTo > costPerPassengerThreeFrom &&
      costPerPassengerFourTo > costPerPassengerFourFrom &&
      costPerPassengerFiveTo > costPerPassengerFiveFrom;

    if (!firstCondition || !secondCondition || !thirdCondition) {
      alert(errorFirstLine + "\n" + errorSecondLine + "\n" + errorThirdLine + "\n" + errorFourthLine + "\n" + errorFifthLine);

      isValid = false;
    }

    return isValid;
  }

  // Validates that all cost per trip fields are in the correct order and that the values are increasing
  function handleSixthValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_100;
    var errorSecondLine = ConstantsTexts.CODE_101;
    var errorThirdLine = ConstantsTexts.CODE_102;
    var errorFourthLine = ConstantsTexts.CODE_103;
    var errorFifthLine = ConstantsTexts.CODE_79;

    const costPerTripOneFrom = Number(inputValueCostPerTripOneFrom);
    const costPerTripOneTo = Number(inputValueCostPerTripOneTo);
    const costPerTripTwoFrom = Number(inputValueCostPerTripTwoFrom);
    const costPerTripTwoTo = Number(inputValueCostPerTripTwoTo);
    const costPerTripThreeFrom = Number(inputValueCostPerTripThreeFrom);
    const costPerTripThreeTo = Number(inputValueCostPerTripThreeTo);
    const costPerTripFourFrom = Number(inputValueCostPerTripFourFrom);
    const costPerTripFourTo = Number(inputValueCostPerTripFourTo);
    const costPerTripFiveFrom = Number(inputValueCostPerTripFiveFrom);
    const costPerTripFiveTo = Number(inputValueCostPerTripFiveTo);

    var firstCondition = costPerTripFiveFrom === 0;
    var secondCondition =
      costPerTripFiveTo === costPerTripFourFrom &&
      costPerTripFourTo === costPerTripThreeFrom &&
      costPerTripThreeTo === costPerTripTwoFrom &&
      costPerTripTwoTo === costPerTripOneFrom;
    var thirdCondition =
      costPerTripOneTo > costPerTripOneFrom &&
      costPerTripTwoTo > costPerTripTwoFrom &&
      costPerTripThreeTo > costPerTripThreeFrom &&
      costPerTripFourTo > costPerTripFourFrom &&
      costPerTripFiveTo > costPerTripFiveFrom;

    if (!firstCondition || !secondCondition || !thirdCondition) {
      alert(errorFirstLine + "\n" + errorSecondLine + "\n" + errorThirdLine + "\n" + errorFourthLine + "\n" + errorFifthLine);

      isValid = false;
    }

    return isValid;
  }

  // handle - logic
  // useWidgetFilterDropdowns - start
  const handleSelectDropdownDistrict = (option) => {
    setSelectedDistrict(option);
    setSelectedPlanningSpace(null);
    setSelectedMunicipality(null);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownPlanningSpace = (option) => {
    setSelectedPlanningSpace(option);
    setSelectedMunicipality(null);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownMunicipality = (option) => {
    setSelectedMunicipality(option);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownTerminal = (option) => {
    setSelectedTerminal(option);
  };
  // useWidgetFilterDropdowns - end

  const handleSearchTerminalIconClick = () => {
    setShowSearchTerminal(!showSearchTerminal);
  };

  const handleShowUpdatesListClick = (value) => {
    setShowUpdatesList(value);
  };

  // Handles cancel action - navigates back if no changes, shows confirmation dialog if changes exist
  const handleCancelModelChanges = () => {
    if (UtilsUnit.areJSONsEqualLoose(jsonEditRefCheckChanges.current, jsonEditRef.current)) {
      // No changes made, just reset create new version state if needed and navigate back
      if (isCreatingNewModelVersion) {
        setIsCreatingNewModelVersion(false);
        clearBaseModelAndSetOriginalSelectedModelVersion();
      } else {
        navigate(-1);
      }
    } else {
      // Store a flag to indicate this is a cancel operation, not a save operation
      lastActionRef.current = ConstantsParams.ActionTypes.CANCEL;
      showEditAlertDialog();
    }
  };

  const showEditAlertDialog = () => {
    setDialogEditOpen(true);
  };

  const closeEditAlertDialog = (cancelEdit) => {
    setDialogEditOpen(false);

    if (cancelEdit) {
      // User confirmed the action
      if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
        // User confirmed cancel - handle based on create new version state
        if (isCreatingNewModelVersion) {
          setIsCreatingNewModelVersion(false);
          clearBaseModel();
          setDataToInputs(data);
        } else {
          navigate(-1);
        }
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
        // User confirmed base model change - proceed with base model change
        handleConfirmBaseModelChange();
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
        // User confirmed model version deletion - proceed with deletion
        handleDeleteModelVersion(modelVersionToDeleteRef.current?.modelVersionInt);
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.FREEZE_VERSION) {
        handleFreezeModelVersion(modelVersionToFreezeRef.current?.modelVersionInt);
      } else {
        // User confirmed save - proceed with save
        handleConfirmSaveModelChanges(lastActionRef.current);
      }
    }
    // If cancelEdit is false, user cancelled - do nothing
  };

  const showSaveAlertDialog = () => {
    setDialogSaveOpen(true);
  };

  const closeSaveAlertDialog = async () => {
    setDialogSaveOpen(false);

    if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      // For base model action, fetch data with modelVersion = 0 and reset base model state
      await fetchData(0);
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      // For delete version action, refresh the data to update the model versions list
      await fetchData(0);
      UtilsUnit.clickOutside();
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.FREEZE_VERSION) {
      showLoadingRef.current();
      try {
        const fetchedData = await ApiService.getModelData(0);
        handleFechedAPIData(fetchedData);
        setSelectedModelVersionFromInt(0, fetchedData?.modelVersions);
      } catch (error) {
        console.error("Error refreshing data after freeze:", error);
      } finally {
        hideLoadingRef.current();
      }
    } else {
      // For create/update actions, use existing logic
      const modelVersionToFetch =
        lastActionRef.current === ConstantsParams.ActionTypes.CREATE ? lastResponseDataRef.current?.modelVersion : getAppropriateModelVersion()?.modelVersionInt;
      await fetchData(modelVersionToFetch);
      setIsCreatingNewModelVersion(false);
    }
  };

  // Handles model version deletion - calls API and shows success dialog
  const handleDeleteModelVersion = async (modelVersionInt) => {
    try {
      showLoadingRef.current();

      const requestBody = {
        modelVersionInt: modelVersionInt,
        userID: UtilsUnit.getUserID(userData),
        userName: UtilsUnit.getUserName(userData),
      };

      const response = await ApiService.deleteModelVersion(requestBody);

      if (response.status === 200) {
        // Show success dialog after successful deletion
        showSaveAlertDialog();
      }
    } catch (error) {
      console.error("Error deleting model version:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Handles delete model version button click - shows confirmation dialog
  const handleDeleteModelVersionClick = (modelVersion) => {
    modelVersionToDeleteRef.current = modelVersion;
    lastActionRef.current = ConstantsParams.ActionTypes.DELETE_VERSION;
    setDialogEditOpen(true);
  };

  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

  const getLatestNonFrozenModelVersionTimestamp = () => {
    let latest = null;

    for (const version of modelVersions) {
      if (!(version.modelVersionInt > 0) || !version.modelVersionTimeStamp || version.isFrozen) {
        continue;
      }

      const raw = version.modelVersionTimeStamp;
      const normalized = typeof raw === "string" ? raw.trim().replace(" ", "T") : raw;
      const versionTime = new Date(normalized).getTime();

      if (Number.isNaN(versionTime)) {
        continue;
      }

      if (latest === null || versionTime > latest) {
        latest = versionTime;
      }
    }

    return latest;
  };

  const getLatestFrozenModelVersionTimestamp = () => {
    let latest = null;

    for (const version of modelVersions) {
      if (!(version.modelVersionInt > 0) || !version.modelVersionTimeStamp || !version.isFrozen) {
        continue;
      }

      const raw = version.modelVersionTimeStamp;
      const normalized = typeof raw === "string" ? raw.trim().replace(" ", "T") : raw;
      const versionTime = new Date(normalized).getTime();

      if (Number.isNaN(versionTime)) {
        continue;
      }

      if (latest === null || versionTime > latest) {
        latest = versionTime;
      }
    }

    return latest;
  };

  const isWithin24HoursOfLatestNonFrozenVersion = () => {
    const latestTimestamp = getLatestNonFrozenModelVersionTimestamp();

    if (!latestTimestamp) {
      return false;
    }

    return Date.now() - latestTimestamp < TWENTY_FOUR_HOURS_MS;
  };

  const isWithin24HoursOfLatestFrozenVersion = () => {
    const latestTimestamp = getLatestFrozenModelVersionTimestamp();

    if (!latestTimestamp) {
      return false;
    }

    return Date.now() - latestTimestamp < TWENTY_FOUR_HOURS_MS;
  };

  const shouldShowCreateModelVersionFooterItem = () => {
    return UtilsUnit.managerUser(userData) && !isWithin24HoursOfLatestNonFrozenVersion();
  };

  const handleFreezeModelVersionClick = () => {
    if (!selectedModelVersion || selectedModelVersion.modelVersionInt !== 0) {
      return;
    }

    modelVersionToFreezeRef.current = selectedModelVersion;
    lastActionRef.current = ConstantsParams.ActionTypes.FREEZE_VERSION;
    setDialogEditOpen(true);
  };

  const handleFreezeModelVersion = async (modelVersionInt) => {
    try {
      showLoadingRef.current();

      // Freeze snapshots DB state for model 0. Persist current UI values first
      // so unsaved edits are not lost / replaced by older defaults.
      if (!handleValidations()) {
        return;
      }

      jsonEditRef.current.userID = UtilsUnit.getUserID(userData);
      jsonEditRef.current.userName = UtilsUnit.getUserName(userData);
      jsonEditRef.current.modelVersion = 0;
      jsonEditRef.current.action = ConstantsParams.ActionTypes.UPDATE;

      const saveResponse = await ApiService.updateModelData(jsonEditRef.current);
      if (saveResponse?.status !== 200) {
        return;
      }

      const requestBody = {
        modelVersionInt: modelVersionInt,
        userID: UtilsUnit.getUserID(userData),
        userName: UtilsUnit.getUserName(userData),
      };

      const response = await ApiService.freezeModelVersion(requestBody);

      if (response.status === 200) {
        lastResponseDataRef.current = response.data;
        hideLoadingRef.current();
        showSaveAlertDialog();
        return;
      }
    } catch (error) {
      console.error("Error freezing model version:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  const hasLiveModelVersionZero = () => {
    return modelVersions.some((version) => version.modelVersionInt === 0 && !version.isFrozen);
  };

  const shouldShowFreezeModelVersionButton = () => {
    return (
      UtilsUnit.managerUser(userData) &&
      !isCreatingNewModelVersion &&
      selectedModelVersion?.modelVersionInt === 0 &&
      hasLiveModelVersionZero() &&
      !isWithin24HoursOfLatestFrozenVersion()
    );
  };

  // Handles input changes with regex validation, removes commas, and updates both state and JSON reference
  const handleChangeRegexNComma = (event, regex, setInputValue, jsonKey) => {
    const value = event.target.value;

    // Remove commas from the input value
    const valueWithoutComma = UtilsUnit.removeCommas(value);

    // Validate the cleaned value
    if (regex.test(valueWithoutComma)) {
      // Update state with the cleaned value
      setInputValue(valueWithoutComma);

      // Update the corresponding JSON field
      jsonEditRef.current[jsonKey] = valueWithoutComma;
    }
  };

  const handleChangeRegex = (event, regex, setInputValue, jsonKey) => {
    const value = event.target.value;

    if (regex.test(value)) {
      setInputValue(value);

      jsonEditRef.current[jsonKey] = value;
    }
  };

  // Handles percentage input changes with validation for 0-100 range and leading zero removal
  const handleChangeRegexPercent = (event, regex, setInputValue, jsonKey) => {
    let value = event.target.value;

    value = value.replace(/^0+(?!$)/, "");

    if (regex.test(value) && /^\d{0,3}$/.test(value) && value <= 100) {
      setInputValue(value);

      jsonEditRef.current[jsonKey] = value;
    }
  };

  const handleChangeSimple = (event, setInputValue, jsonKey) => {
    const value = event.target.value;

    setInputValue(value);

    jsonEditRef.current[jsonKey] = value;
  };

  // handleChange general
  const handleChangeLifeTime = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueLifeTime, "life_time");
  };

  const handleChangeBusinessDays = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueBusinessDays, "business_days");
  };

  const handleChangeMetropolitanRingOne = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMetropolitanRingOne, "metropolitan_ring_one");
  };

  const handleChangeMetropolitanRingTwo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMetropolitanRingTwo, "metropolitan_ring_two");
  };

  const handleChangeMetropolitanRingThree = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMetropolitanRingThree, "metropolitan_ring_three");
  };

  const handleChangeMetropolitanRingFour = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMetropolitanRingFour, "metropolitan_ring_four");
  };

  const handleChangeCostPerAcre = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostPerAcre, "cost_per_acre");
  };

  // handleChange Percent
  const handleChangeUrgencyPercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValueUrgencyPercent, "urgency_percent");
  };

  const handleChangeImportanceServicePercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValueImportanceServicePercent, "importance_service_percent");
  };

  const handleChangePotentialPercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValuePotentialPercent, "potential_percent");
  };

  const handleChangeReducingOperationalGapPercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValueReducingOperationalGapPercent, "reducing_operational_gap_percent");
  };

  const handleChangeCostPerPassengerPercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValueCostPerPassengerPercent, "cost_per_passenger_percent");
  };

  const handleChangeCostPerTripPercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValueCostPerTripPercent, "cost_per_trip_percent");
  };

  const handleChangePositionLocalAuthorityPercent = (event) => {
    handleChangeRegexPercent(event, UtilsUnit.regexInt, setInputValuePositionLocalAuthorityPercent, "position_local_authority_percent");
  };

  // handleChange One
  const handleChangeUrgencyOne = (event) => {
    handleChangeSimple(event, setInputValueUrgencyOne, "urgency_one");
  };

  const handleChangeImportanceServiceOneFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceOneFrom, "importance_service_one_from");
  };

  const handleChangeImportanceServiceOneTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceOneTo, "importance_service_one_to");
  };

  const handleChangePotentialOneFrom = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialOneFrom, "potential_one_from");
  };

  const handleChangePotentialOneTo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialOneTo, "potential_one_to");
  };

  const handleChangeReducingOperationalGapOneFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapOneFrom, "reducing_operational_gap_one_from");
  };

  const handleChangeReducingOperationalGapOneTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapOneTo, "reducing_operational_gap_one_to");
  };

  const handleChangeCostPerPassengerOneFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerOneFrom, "cost_per_passenger_one_from");
  };

  const handleChangeCostPerPassengerOneTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerOneTo, "cost_per_passenger_one_to");
  };

  const handleChangeCostPerTripOneFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripOneFrom, "cost_per_trip_one_from");
  };

  const handleChangeCostPerTripOneTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripOneTo, "cost_per_trip_one_to");
  };

  // handleChange Two
  const handleChangeUrgencyTwo = (event) => {
    handleChangeSimple(event, setInputValueUrgencyTwo, "urgency_two");
  };

  const handleChangeImportanceServiceTwoFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceTwoFrom, "importance_service_two_from");
  };

  const handleChangeImportanceServiceTwoTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceTwoTo, "importance_service_two_to");
  };

  const handleChangePotentialTwoFrom = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialTwoFrom, "potential_two_from");
  };

  const handleChangePotentialTwoTo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialTwoTo, "potential_two_to");
  };

  const handleChangeReducingOperationalGapTwoFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapTwoFrom, "reducing_operational_gap_two_from");
  };

  const handleChangeReducingOperationalGapTwoTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapTwoTo, "reducing_operational_gap_two_to");
  };

  const handleChangeCostPerPassengerTwoFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerTwoFrom, "cost_per_passenger_two_from");
  };

  const handleChangeCostPerPassengerTwoTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerTwoTo, "cost_per_passenger_two_to");
  };

  const handleChangeCostPerTripTwoFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripTwoFrom, "cost_per_trip_two_from");
  };

  const handleChangeCostPerTripTwoTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripTwoTo, "cost_per_trip_two_to");
  };

  // handleChange Three
  const handleChangeUrgencyThree = (event) => {
    handleChangeSimple(event, setInputValueUrgencyThree, "urgency_three");
  };

  const handleChangeImportanceServiceThreeFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceThreeFrom, "importance_service_three_from");
  };

  const handleChangeImportanceServiceThreeTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceThreeTo, "importance_service_three_to");
  };

  const handleChangePotentialThreeFrom = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialThreeFrom, "potential_three_from");
  };

  const handleChangePotentialThreeTo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialThreeTo, "potential_three_to");
  };

  const handleChangeReducingOperationalGapThreeFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapThreeFrom, "reducing_operational_gap_three_from");
  };

  const handleChangeReducingOperationalGapThreeTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapThreeTo, "reducing_operational_gap_three_to");
  };

  const handleChangeCostPerPassengerThreeFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerThreeFrom, "cost_per_passenger_three_from");
  };

  const handleChangeCostPerPassengerThreeTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerThreeTo, "cost_per_passenger_three_to");
  };

  const handleChangeCostPerTripThreeFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripThreeFrom, "cost_per_trip_three_from");
  };

  const handleChangeCostPerTripThreeTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripThreeTo, "cost_per_trip_three_to");
  };

  // handleChange Four
  const handleChangeUrgencyFour = (event) => {
    handleChangeSimple(event, setInputValueUrgencyFour, "urgency_four");
  };

  const handleChangeImportanceServiceFourFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceFourFrom, "importance_service_four_from");
  };

  const handleChangeImportanceServiceFourTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceFourTo, "importance_service_four_to");
  };

  const handleChangePotentialFourFrom = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialFourFrom, "potential_four_from");
  };

  const handleChangePotentialFourTo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialFourTo, "potential_four_to");
  };

  const handleChangeReducingOperationalGapFourFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapFourFrom, "reducing_operational_gap_four_from");
  };

  const handleChangeReducingOperationalGapFourTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapFourTo, "reducing_operational_gap_four_to");
  };

  const handleChangeCostPerPassengerFourFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerFourFrom, "cost_per_passenger_four_from");
  };

  const handleChangeCostPerPassengerFourTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerFourTo, "cost_per_passenger_four_to");
  };

  const handleChangeCostPerTripFourFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripFourFrom, "cost_per_trip_four_from");
  };

  const handleChangeCostPerTripFourTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripFourTo, "cost_per_trip_four_to");
  };

  // handleChange Five
  const handleChangeUrgencyFive = (event) => {
    handleChangeSimple(event, setInputValueUrgencyFive, "urgency_five");
  };

  const handleChangeImportanceServiceFiveFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceFiveFrom, "importance_service_five_from");
  };

  const handleChangeImportanceServiceFiveTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueImportanceServiceFiveTo, "importance_service_five_to");
  };

  const handleChangePotentialFiveFrom = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialFiveFrom, "potential_five_from");
  };

  const handleChangePotentialFiveTo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialFiveTo, "potential_five_to");
  };

  const handleChangeReducingOperationalGapFiveFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapFiveFrom, "reducing_operational_gap_five_from");
  };

  const handleChangeReducingOperationalGapFiveTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueReducingOperationalGapFiveTo, "reducing_operational_gap_five_to");
  };

  const handleChangeCostPerPassengerFiveFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerFiveFrom, "cost_per_passenger_five_from");
  };

  const handleChangeCostPerPassengerFiveTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexDecimalTwoNumber, setInputValueCostPerPassengerFiveTo, "cost_per_passenger_five_to");
  };

  const handleChangeCostPerTripFiveFrom = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripFiveFrom, "cost_per_trip_five_from");
  };

  const handleChangeCostPerTripFiveTo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueCostPerTripFiveTo, "cost_per_trip_five_to");
  };

  // handleChange Costs
  // handleChange Costs One
  const handleChangeMonthsOfWorkOne = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMonthsOfWorkOne, "months_of_work_one");
  };

  const handleChangeCostOfBuildingsOne = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfBuildingsOne, "cost_of_buildings_one");
  };

  const handleChangeCostOfTrafficOne = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfTrafficOne, "cost_of_traffic_one");
  };

  // handleChange Costs Two
  const handleChangeMonthsOfWorkTwo = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMonthsOfWorkTwo, "months_of_work_two");
  };

  const handleChangeCostOfBuildingsTwo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfBuildingsTwo, "cost_of_buildings_two");
  };

  const handleChangeCostOfTrafficTwo = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfTrafficTwo, "cost_of_traffic_two");
  };

  // handleChange Costs Three
  const handleChangeMonthsOfWorkThree = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMonthsOfWorkThree, "months_of_work_three");
  };

  const handleChangeCostOfBuildingsThree = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfBuildingsThree, "cost_of_buildings_three");
  };

  const handleChangeCostOfTrafficThree = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfTrafficThree, "cost_of_traffic_three");
  };

  // handleChange Costs Four
  const handleChangeMonthsOfWorkFour = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMonthsOfWorkFour, "months_of_work_four");
  };

  const handleChangeCostOfBuildingsFour = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfBuildingsFour, "cost_of_buildings_four");
  };

  const handleChangeCostOfTrafficFour = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfTrafficFour, "cost_of_traffic_four");
  };

  // handleChange Costs Five
  const handleChangeMonthsOfWorkFive = (event) => {
    handleChangeRegex(event, UtilsUnit.regexInt, setInputValueMonthsOfWorkFive, "months_of_work_five");
  };

  const handleChangeCostOfBuildingsFive = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfBuildingsFive, "cost_of_buildings_five");
  };

  const handleChangeCostOfTrafficFive = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueCostOfTrafficFive, "cost_of_traffic_five");
  };

  const isModelVersionGreaterThanZeroOrCreatingNew = () => {
    return getAppropriateModelVersion()?.modelVersionInt > 0 || isCreatingNewModelVersion;
  };

  const handleCreateModelVersion = () => {
    if (isWithin24HoursOfLatestNonFrozenVersion()) {
      alert(ConstantsTexts.CODE_247);
      return;
    }

    setIsCreatingNewModelVersion(true);
  };

  // Toggles base model selection mode, handles version 0 validation, and manages dropdown state
  const handleBaseModelChange = async (e) => {
    // Toggle the base model selected state
    const newIsBaseModelSelected = !isBaseModelSelected;

    if (newIsBaseModelSelected) {
      setDropdownType(DropdownType.SINGLE_SELECT);

      // Store the original selected model version before changing it
      originalSelectedModelVersionRef.current = selectedModelVersion;
      setIsBaseModelSelected(true);

      // Set the model version dropdown to version 0 as default
      const modelVersionZero = modelVersions?.find((version) => version.modelVersionInt === 0);

      if (modelVersionZero) {
        setSelectedModelVersion(modelVersionZero);
        // Reset the ref to version 0 when entering base model selection mode
        baseModelSelectedVersionRef.current = modelVersionZero;
      }
    } else {
      // Check if the selected version is 0 and show alert if it is
      if (baseModelSelectedVersionRef.current?.modelVersionInt === 0) {
        alert(ConstantsTexts.CODE_170);
        return;
      }

      // Set last action and show confirmation dialog
      lastActionRef.current = ConstantsParams.ActionTypes.BASE_MODEL;
      showEditAlertDialog();
      return;
    }

    setIsBaseModelSelected(newIsBaseModelSelected);
  };

  const handleConfirmBaseModelChange = async () => {
    try {
      showLoadingRef.current();

      const requestBody = {
        modelVersion: baseModelSelectedVersionRef.current?.modelVersionInt,
        userID: UtilsUnit.getUserID(userData),
        userName: UtilsUnit.getUserName(userData),
      };

      const response = await ApiService.makeModelAsZero(requestBody);

      if (response.status === 200) {
        showSaveAlertDialog();
      }
    } catch (error) {
      console.error("Error calling makeModelAsZero API:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  const handleBaseModelClose = () => {
    clearBaseModelAndSetOriginalSelectedModelVersion();
    UtilsUnit.clickOutside();
  };

  const clearBaseModel = () => {
    setDropdownType(DropdownType.DEFAULT_SELECT);
    setIsBaseModelSelected(false);
    originalSelectedModelVersionRef.current = null;
    baseModelSelectedVersionRef.current = null;
  };

  const clearBaseModelAndSetOriginalSelectedModelVersion = () => {
    if (originalSelectedModelVersionRef.current) {
      setSelectedModelVersion(originalSelectedModelVersionRef.current);
      clearBaseModel();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSaveModelChanges(isCreatingNewModelVersion ? ConstantsParams.ActionTypes.CREATE : ConstantsParams.ActionTypes.UPDATE);
  };

  const setSelectedModelVersionFromInt = (modelVersionInt, modelVersions) => {
    const matchingModelVersion = modelVersions?.find((version) => version.modelVersionInt === modelVersionInt);
    setSelectedModelVersion(matchingModelVersion);
  };

  // Get dialog text based on action
  const getDialogEditCancelText = () => {
    if (lastActionRef.current === ConstantsParams.ActionTypes.CREATE || lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      return ConstantsTexts.CODE_7;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
      return ConstantsTexts.CODE_61;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      return ConstantsTexts.CODE_7;
    }
  };

  const getDialogSaveMessageText = () => {
    if (lastActionRef.current === ConstantsParams.ActionTypes.CREATE) {
      return ConstantsTexts.CODE_160.replace("{version}", lastResponseDataRef.current?.modelVersion);
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.UPDATE) {
      return ConstantsTexts.CODE_62;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      return ConstantsTexts.CODE_168;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      return ConstantsTexts.CODE_212.replace("{modelNumber}", modelVersionToDeleteRef.current?.modelVersionInt);
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.FREEZE_VERSION) {
      return ConstantsTexts.CODE_246.replace("{version}", lastResponseDataRef.current?.modelVersion);
    }
  };

  const getDialogEditMessageText = () => {
    if (lastActionRef.current === ConstantsParams.ActionTypes.CREATE) {
      return ConstantsTexts.CODE_161;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
      return ConstantsTexts.CODE_59;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      return ConstantsTexts.CODE_171.replace("{version}", baseModelSelectedVersionRef.current?.modelVersionInt);
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      return ConstantsTexts.CODE_211.replace("{modelNumber}", modelVersionToDeleteRef.current?.modelVersionInt);
    }
  };

  const getDialogEditApproveText = () => {
    if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      return ConstantsTexts.CODE_173;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
      return ConstantsTexts.CODE_60;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      return ConstantsTexts.CODE_207;
    }
  };

  const shouldShowModelVersionCustomFooterItemSecond = () => {
    return UtilsUnit.managerUser(userData) && modelVersions.length > 1;
  };

  // Helper function to check if base model is selected and original selected model version ref is not null
  const isBaseModelSelectedAndOriginalSelectedModelVersionRef = () => {
    return isBaseModelSelected && originalSelectedModelVersionRef.current;
  };

  // Helper function to get the appropriate model version (original or selected)
  const getAppropriateModelVersion = () => {
    return isBaseModelSelectedAndOriginalSelectedModelVersionRef() ? originalSelectedModelVersionRef.current : selectedModelVersion;
  };

  // Handle closing the update model fields dialog and refresh data
  const handleCloseUpdateModelFieldsDialog = () => {
    setDialogUpdateModelFieldsOpen(false);
    fetchData(getAppropriateModelVersion()?.modelVersionInt);
  };

  return {
    dataIsReceived,
    getMainMeasurePercent,
    inputValueUrgencyPercent,
    handleChangeUrgencyPercent,
    inputValueImportanceServicePercent,
    handleChangeImportanceServicePercent,
    inputValuePotentialPercent,
    handleChangePotentialPercent,
    inputValueReducingOperationalGapPercent,
    handleChangeReducingOperationalGapPercent,
    inputValueUrgencyOne,
    handleChangeUrgencyOne,
    inputValueImportanceServiceOneFrom,
    inputValueImportanceServiceOneTo,
    handleChangeImportanceServiceOneFrom,
    handleChangeImportanceServiceOneTo,
    inputValuePotentialOneFrom,
    inputValuePotentialOneTo,
    handleChangePotentialOneFrom,
    handleChangePotentialOneTo,
    inputValueReducingOperationalGapOneFrom,
    inputValueReducingOperationalGapOneTo,
    handleChangeReducingOperationalGapOneFrom,
    handleChangeReducingOperationalGapOneTo,
    inputValueUrgencyTwo,
    handleChangeUrgencyTwo,
    inputValueImportanceServiceTwoFrom,
    inputValueImportanceServiceTwoTo,
    handleChangeImportanceServiceTwoFrom,
    handleChangeImportanceServiceTwoTo,
    inputValuePotentialTwoFrom,
    inputValuePotentialTwoTo,
    handleChangePotentialTwoFrom,
    handleChangePotentialTwoTo,
    inputValueReducingOperationalGapTwoFrom,
    inputValueReducingOperationalGapTwoTo,
    handleChangeReducingOperationalGapTwoFrom,
    handleChangeReducingOperationalGapTwoTo,
    inputValueUrgencyThree,
    handleChangeUrgencyThree,
    inputValueImportanceServiceThreeFrom,
    inputValueImportanceServiceThreeTo,
    handleChangeImportanceServiceThreeFrom,
    handleChangeImportanceServiceThreeTo,
    inputValuePotentialThreeFrom,
    inputValuePotentialThreeTo,
    handleChangePotentialThreeFrom,
    handleChangePotentialThreeTo,
    inputValueReducingOperationalGapThreeFrom,
    inputValueReducingOperationalGapThreeTo,
    handleChangeReducingOperationalGapThreeFrom,
    handleChangeReducingOperationalGapThreeTo,
    inputValueUrgencyFour,
    handleChangeUrgencyFour,
    inputValueImportanceServiceFourFrom,
    inputValueImportanceServiceFourTo,
    handleChangeImportanceServiceFourFrom,
    handleChangeImportanceServiceFourTo,
    inputValuePotentialFourFrom,
    inputValuePotentialFourTo,
    handleChangePotentialFourFrom,
    handleChangePotentialFourTo,
    inputValueReducingOperationalGapFourFrom,
    inputValueReducingOperationalGapFourTo,
    handleChangeReducingOperationalGapFourFrom,
    handleChangeReducingOperationalGapFourTo,
    inputValueUrgencyFive,
    handleChangeUrgencyFive,
    inputValueImportanceServiceFiveFrom,
    inputValueImportanceServiceFiveTo,
    handleChangeImportanceServiceFiveFrom,
    handleChangeImportanceServiceFiveTo,
    inputValuePotentialFiveFrom,
    inputValuePotentialFiveTo,
    handleChangePotentialFiveFrom,
    handleChangePotentialFiveTo,
    inputValueReducingOperationalGapFiveFrom,
    inputValueReducingOperationalGapFiveTo,
    handleChangeReducingOperationalGapFiveFrom,
    handleChangeReducingOperationalGapFiveTo,
    handleCancelModelChanges,
    inputValueLifeTime,
    handleChangeLifeTime,
    inputValueBusinessDays,
    handleChangeBusinessDays,
    inputValuePositionLocalAuthorityPercent,
    handleChangePositionLocalAuthorityPercent,
    inputValueMetropolitanRingOne,
    handleChangeMetropolitanRingOne,
    inputValueMetropolitanRingTwo,
    handleChangeMetropolitanRingTwo,
    inputValueMetropolitanRingThree,
    handleChangeMetropolitanRingThree,
    inputValueMetropolitanRingFour,
    handleChangeMetropolitanRingFour,
    inputValueCostPerAcre,
    handleChangeCostPerAcre,
    workMonthsText,
    inputValueMonthsOfWorkOne,
    handleChangeMonthsOfWorkOne,
    inputValueCostOfBuildingsOne,
    handleChangeCostOfBuildingsOne,
    inputValueCostOfTrafficOne,
    handleChangeCostOfTrafficOne,
    getTotalPriceForTrafficArrangementsPerTerminalTypeNIS,
    inputValueMonthsOfWorkTwo,
    handleChangeMonthsOfWorkTwo,
    inputValueCostOfBuildingsTwo,
    handleChangeCostOfBuildingsTwo,
    inputValueCostOfTrafficTwo,
    handleChangeCostOfTrafficTwo,
    inputValueMonthsOfWorkThree,
    handleChangeMonthsOfWorkThree,
    inputValueCostOfBuildingsThree,
    handleChangeCostOfBuildingsThree,
    inputValueCostOfTrafficThree,
    handleChangeCostOfTrafficThree,
    inputValueMonthsOfWorkFour,
    handleChangeMonthsOfWorkFour,
    inputValueCostOfBuildingsFour,
    handleChangeCostOfBuildingsFour,
    inputValueCostOfTrafficFour,
    handleChangeCostOfTrafficFour,
    inputValueMonthsOfWorkFive,
    handleChangeMonthsOfWorkFive,
    inputValueCostOfBuildingsFive,
    handleChangeCostOfBuildingsFive,
    inputValueCostOfTrafficFive,
    handleChangeCostOfTrafficFive,
    inputValueCostPerPassengerPercent,
    handleChangeCostPerPassengerPercent,
    inputValueCostPerTripPercent,
    handleChangeCostPerTripPercent,
    inputValueCostPerPassengerOneFrom,
    inputValueCostPerPassengerOneTo,
    handleChangeCostPerPassengerOneFrom,
    handleChangeCostPerPassengerOneTo,
    inputValueCostPerTripOneFrom,
    inputValueCostPerTripOneTo,
    handleChangeCostPerTripOneFrom,
    handleChangeCostPerTripOneTo,
    inputValueCostPerPassengerTwoFrom,
    inputValueCostPerPassengerTwoTo,
    handleChangeCostPerPassengerTwoFrom,
    handleChangeCostPerPassengerTwoTo,
    inputValueCostPerTripTwoFrom,
    inputValueCostPerTripTwoTo,
    handleChangeCostPerTripTwoFrom,
    handleChangeCostPerTripTwoTo,
    inputValueCostPerPassengerThreeFrom,
    inputValueCostPerPassengerThreeTo,
    handleChangeCostPerPassengerThreeFrom,
    handleChangeCostPerPassengerThreeTo,
    inputValueCostPerTripThreeFrom,
    inputValueCostPerTripThreeTo,
    handleChangeCostPerTripThreeFrom,
    handleChangeCostPerTripThreeTo,
    inputValueCostPerPassengerFourFrom,
    inputValueCostPerPassengerFourTo,
    handleChangeCostPerPassengerFourFrom,
    handleChangeCostPerPassengerFourTo,
    inputValueCostPerTripFourFrom,
    inputValueCostPerTripFourTo,
    handleChangeCostPerTripFourFrom,
    handleChangeCostPerTripFourTo,
    inputValueCostPerPassengerFiveFrom,
    inputValueCostPerPassengerFiveTo,
    handleChangeCostPerPassengerFiveFrom,
    handleChangeCostPerPassengerFiveTo,
    inputValueCostPerTripFiveFrom,
    inputValueCostPerTripFiveTo,
    handleChangeCostPerTripFiveFrom,
    handleChangeCostPerTripFiveTo,
    buildingCostText,
    monthlyTrafficArrangementCostText,
    totalTrafficArrangementCostText,
    modelChangesLog,
    isDialogEditOpen,
    closeEditAlertDialog,
    isDialogSaveOpen,
    closeSaveAlertDialog,
    isDialogUpdateModelFieldsOpen,
    setDialogUpdateModelFieldsOpen,
    getDialogEditCancelText,
    getDialogSaveMessageText,
    getDialogEditMessageText,
    getDialogEditApproveText,
    showUpdatesList,
    handleShowUpdatesListClick,
    handleSearchTerminalIconClick,
    uniqueDistricts,
    handleSelectDropdownDistrict,
    selectedDistrict,
    uniquePlanningSpace,
    handleSelectDropdownPlanningSpace,
    selectedPlanningSpace,
    uniqueMunicipalities,
    handleSelectDropdownMunicipality,
    selectedMunicipality,
    uniqueTerminals,
    handleSelectDropdownTerminal,
    selectedTerminal,
    showSearchTerminal,
    modelVersions,
    handleSelectModelVersion,
    selectedModelVersion,
    isModelVersionGreaterThanZeroOrCreatingNew,
    isCreatingNewModelVersion,
    handleCreateModelVersion,
    handleBaseModelChange,
    handleBaseModelClose,
    isBaseModelSelected,
    dropdownType,
    userData,
    handleFormSubmit,
    shouldShowModelVersionCustomFooterItemSecond,
    handleDeleteModelVersionClick,
    handleFreezeModelVersionClick,
    shouldShowFreezeModelVersionButton,
    shouldShowCreateModelVersionFooterItem,
    lastActionRef,
    modelVersionToFreezeRef,
    handleCloseUpdateModelFieldsDialog,
  };
}
